// mvp-returns Phase H17 — Failed Operations admin page

import { Anchor, Box, Group, MultiSelect, Pagination, Select, Stack, Switch, Table, Text, Tooltip } from "@mantine/core";
import { i18n } from "@dehub/theme";
import { ArrowCounterClockwiseIcon, CheckIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { FailedOpsApi } from "../../../apis";
import type { FailedOpStatus, IFailedOp } from "../../../apis/failed-ops";
import { DeliveryRowAction } from "../../../components/delivery/action-controls";
import { TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import {
  GiveUpFailedOpModal,
  ResolveFailedOpModal,
  RetryNowModal,
} from "../../../components/return-settings/failed-ops-action-modals";
import { FailedOpsStatusBadge } from "../../../components/return-settings/failed-ops-status-badge";
import { DeniedCard, ListState } from "../../../components/ui";
import { dateTimeSecFormat } from "../../../utils/date";

const PAGE_SIZE = 20;

// Lining figures so codes and dates align down a column.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

type TFn = (k: string, o?: Record<string, unknown>) => string;

// The operation types the BE parks on the queue (constants/FAILED_OPERATION_TYPES + the SLA schedulers).
const OPERATION_TYPES = [
  "return-pickup-sla",
  "warehouse-receipt-sla",
  "stuck-return-detector",
  "RETURN_RESTOCK",
  "RETURN_RESTOCK_UNLINKED",
  "RETURN_RESOLUTION_RETRY",
];

const breadcrumb = () => [
  { to: "/",                 label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns",          label: i18n.t("returns:breadcrumb.returns") },
  { to: "/returns/settings", label: i18n.t("returns:breadcrumb.settings") },
  { label: i18n.t("returns:failedOps.title") },
];

const statusOptions = (t: TFn): { value: FailedOpStatus; label: string }[] =>
  (["PENDING", "RETRYING", "MANUAL_INTERVENTION_REQUIRED", "RESOLVED", "GIVEN_UP"] as FailedOpStatus[])
    .map((value) => ({ value, label: t(`failedOps.status.${value}`) }));

const ACTIONABLE_STATUSES: FailedOpStatus[] = ["PENDING", "MANUAL_INTERVENTION_REQUIRED"];

const TERMINAL_STATUSES = new Set(["RESOLVED", "GIVEN_UP"]);

type ModalKind = "retry" | "resolve" | "giveUp" | null;

export function FailedOperationsPage() {
  const { t } = useTranslation("returns");
  // BE failed-operations: list = RETURN_VIEW; retry / resolve / give-up = RETURN_FAILED_OP_MNG (SUPPLIER portal, O170).
  const view = usePermission("RETURN_VIEW");
  const override = usePermission("RETURN_FAILED_OP_MNG");

  const [page, setPage]                     = useState(1);
  const [statusFilter, setStatusFilter]     = useState<FailedOpStatus[]>([]);
  const [opTypeFilter, setOpTypeFilter]     = useState<string | null>(null);
  const [onlyActionable, setOnlyActionable] = useState(false);

  const [activeModal, setActiveModal] = useState<ModalKind>(null);
  const [targetOp, setTargetOp]       = useState<IFailedOp | null>(null);

  const status: FailedOpStatus[] | undefined = onlyActionable
    ? ACTIONABLE_STATUSES
    : statusFilter.length > 0 ? statusFilter : undefined;

  const { data, error, isLoading, mutate } = useSWR(
    view.hasAccess ? ["failed-operations", page, status?.join(","), opTypeFilter] : null,
    () => FailedOpsApi.list({ status, operationType: opTypeFilter ?? undefined, page, limit: PAGE_SIZE }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );
  const rows = data?.items ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const hasFilter = !!status || !!opTypeFilter;

  const opTypeOptions = OPERATION_TYPES.map((value) => ({ value, label: t(`failedOps.opType.${value}`) }));
  const opLabel = (code: string) => t(`failedOps.opType.${code}`, { defaultValue: code });

  const openModal = (kind: ModalKind, op: IFailedOp) => { setTargetOp(op); setActiveModal(kind); };
  const closeModal = () => { setActiveModal(null); setTargetOp(null); };

  // O171 / O172 — actions stay visible; no permission, a closed row or a non-retryable notice makes them aria-disabled with the reason.
  const actionState = (row: IFailedOp, action: "retry" | "resolve" | "giveUp") => {
    if (!override.hasAccess) return { allowed: false, reason: override.reason };
    if (TERMINAL_STATUSES.has(row.status)) return { allowed: false, reason: t("failedOps.reason.closed") };
    if (action === "retry" && row.retryable === false) return { allowed: false, reason: t("failedOps.reason.notRetryable") };
    return { allowed: true, reason: null };
  };

  if (!view.hasAccess) {
    return (
      <PageLayout title={t("failedOps.title")} breadcrumb={breadcrumb()}>
        <DeniedCard permission="RETURN_VIEW" />
      </PageLayout>
    );
  }

  const clearFilters = () => {
    setStatusFilter([]);
    setOpTypeFilter(null);
    setOnlyActionable(false);
    setPage(1);
  };

  const footer = (
    <>
      <Text span style={TNUM}>{rows.length ? `${(page - 1) * PAGE_SIZE + 1}-${(page - 1) * PAGE_SIZE + rows.length} / ${total}` : `0 / ${total}`}</Text>
      {totalPages > 1 ? <Pagination value={page} total={totalPages} onChange={setPage} size="sm" /> : <Box />}
    </>
  );

  return (
    <PageLayout
      title={t("failedOps.title")}
      subTitle={t("failedOps.subtitle")}
      breadcrumb={breadcrumb()}
    >
      <Stack gap="md">
        {/* Filters */}
        <Group gap="sm" wrap="wrap" align="flex-end">
          <MultiSelect
            label={t("failedOps.statusLabel")}
            placeholder={t("trustList.all")}
            data={statusOptions(t)}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v as FailedOpStatus[]); setPage(1); }}
            disabled={onlyActionable}
            clearable
            w={280}
          />
          <Select
            label={t("failedOps.opTypeLabel")}
            placeholder={t("trustList.all")}
            data={opTypeOptions}
            value={opTypeFilter}
            onChange={(v) => { setOpTypeFilter(v); setPage(1); }}
            clearable
            w={260}
          />
          <Box pb={6}>
            <Switch
              label={t("failedOps.onlyActionable")}
              checked={onlyActionable}
              onChange={(e) => {
                setOnlyActionable(e.currentTarget.checked);
                if (e.currentTarget.checked) setStatusFilter([]);
                setPage(1);
              }}
            />
          </Box>
        </Group>

        <TableFrame footer={rows.length ? footer : undefined}>
          {error && !data ? (
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : isLoading && !data ? (
            <ListState state="loading" columns={8} />
          ) : rows.length === 0 ? (
            hasFilter ? (
              <ListState state="noResults" action={{ onClick: clearFilters }} />
            ) : (
              <ListState
                state="empty"
                title={t("failedOps.empty")}
                description={t("failedOps.emptyHint")}
                icon={<WarningCircleIcon size={24} />}
              />
            )
          ) : (
            <Table.ScrollContainer minWidth={1100}>
              <Table layout="fixed" highlightOnHover verticalSpacing="sm" horizontalSpacing="sm" styles={{ td: { verticalAlign: "middle" } }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={120}>{t("preventionTable.actions")}</Table.Th>
                    <Table.Th>{t("failedOps.opTypeLabel")}</Table.Th>
                    <Table.Th w={96}>{t("failedOps.col.targetService")}</Table.Th>
                    <Table.Th w={150}>{t("breadcrumb.returns")}</Table.Th>
                    <Table.Th w={224}>{t("failedOps.col.statusAttempts")}</Table.Th>
                    <Table.Th w={200}>{t("failedOps.col.lastError")}</Table.Th>
                    <Table.Th w={224}>{t("failedOps.col.nextAttempt")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <DeliveryRowAction label={t("failedOps.retry")} icon={<ArrowCounterClockwiseIcon />} state={actionState(row, "retry")} onClick={() => openModal("retry", row)} />
                          <DeliveryRowAction label={t("failedOps.resolveModal.title")} icon={<CheckIcon />} state={actionState(row, "resolve")} onClick={() => openModal("resolve", row)} />
                          <DeliveryRowAction label={t("resolutionPanel.cancel")} icon={<XIcon />} state={actionState(row, "giveUp")} onClick={() => openModal("giveUp", row)} />
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{opLabel(row.operationType)}</Text>
                        <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" translate="no">{row.operationType}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.targetService ? t(`failedOps.target.${row.targetService}`, { defaultValue: row.targetService }) : "-"}</Text>
                      </Table.Td>
                      <Table.Td>
                        {row.returnRequestId && row.returnNumber ? (
                          <Anchor component={Link} to={`/returns/${row.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no" style={{ whiteSpace: "nowrap" }}>
                            {row.returnNumber}
                          </Anchor>
                        ) : <Text size="sm">-</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={4} align="flex-start">
                          <FailedOpsStatusBadge status={row.status} />
                          <Text size="sm" c="var(--nw-text-2)">
                            {t("failedOps.attempts")} <Text span ff="monospace" fz="var(--nw-fz-mono)" style={TNUM}>{row.retryCount}/{row.maxRetries}</Text>
                          </Text>
                        </Stack>
                      </Table.Td>
                      {/* The last error is stored by the BE as raw text: shown clamped, whole text in the tooltip. */}
                      <Table.Td maw={240}>
                        {row.lastError ? (
                          <Tooltip label={row.lastError} withArrow multiline maw={360} position="top-start">
                            <Text size="sm" lineClamp={2}>{row.lastError}</Text>
                          </Tooltip>
                        ) : <Text size="sm">-</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace" style={{ ...TNUM, whiteSpace: "nowrap" }}>{dateTimeSecFormat(row.nextAttemptAt)}</Text>
                        <Text size="sm" c="var(--nw-text-2)" style={{ whiteSpace: "nowrap" }}>
                          {t("failedOps.createdPrefix")} <Text span ff="monospace" fz="var(--nw-fz-mono)" style={TNUM}>{dateTimeSecFormat(row.createdAt)}</Text>
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </TableFrame>
      </Stack>

      {/* Action modals (440) */}
      {targetOp && (
        <>
          <RetryNowModal opened={activeModal === "retry"} onClose={closeModal} opId={targetOp.id} onSuccess={() => mutate()} />
          <ResolveFailedOpModal opened={activeModal === "resolve"} onClose={closeModal} opId={targetOp.id} onSuccess={() => mutate()} />
          <GiveUpFailedOpModal opened={activeModal === "giveUp"} onClose={closeModal} opId={targetOp.id} onSuccess={() => mutate()} />
        </>
      )}
    </PageLayout>
  );
}
